<script setup>
import {reactive, onMounted, computed, ref} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {Permissions} from "@/Utils/Permissions";
import {ToastUtil} from "@/Utils/ToastUtil";

const props = defineProps({
  contextIdA: String
});

const form = ref(null);

const data = reactive({
  dataLoading: true,
  dialog: false,
  dialog2: false,
  editedItem: null,
  isEditing: false,
  editId: null,
  editAmount: null,
  search: '',
  headers: [
    { title: 'Invoice Number', key: 'invoiceid' },
    { title: 'Invoice Date', key: 'invoicedate' },
    { title: 'Original Invoice Amount', key: 'total' },
    { title: 'Payment Amount', key: 'amount' },
    { title: 'Pending Invoice Balance', key: 'balance' },
    { title: 'Payment Date', key: 'date' },
    { title: 'Check Number', key: 'checknumber' },
    { title: 'Client', key: 'clientid' },
    { title: 'Actions', key: 'actions'}
  ],
  items: [],
  singleBatch: {},
  statusUpdating: false
})

const csvData = computed(()=>{
  // CSV File
  return data.items.map((item) => ({
    InvoiceNumber: item.invoiceid,
    ClaimNumber: item.claimnumber,
    PaymentAmount: item.amount !== null ? item.amount.replace(/,/g, "") : item.amount,
    PaymentDate: item.dateformatted,
    CheckNumber: item.checknumber,
    InvoiceDate: item.invoicedateformatted,
    Total: item.total !== null ? item.total.replace(/,/g, "") : item.total,
    PendingAmount: item.balance !== null ? item.balance.replace(/,/g, "") : item.balance,
    OriginalInvoiceAmount: item.total !== null ? item.total.replace(/,/g, "") : item.total,
    ClientName: item.clientname
  }))
})

const csvExport = (arrData) => {
  // CSV File
  let csvContent = 'data:text/csv;charset=utf-8,'
  csvContent+= 'Batch '+props.contextIdA+' Payments\n';
  csvContent += [
    Object.keys(arrData[0]).join(','),
    ...arrData.map((item) => Object.values(item).join(',')),
  ]
      .join('\n')
      .replace(/(^\[)|(\]$)/gm, '')

  const csvData = encodeURI(csvContent).replaceAll('#', '%23');
  const link = document.createElement('a')
  link.setAttribute('href', csvData)
  link.setAttribute('download', 'Batch '+props.contextIdA+' Payments.csv');
  link.click()
}

const formatISODateToAmerican = (date) => {
  if (!date) return null
  const [year, month, day] = date.split('-')
  return `${month}/${day}/${year}`
}

const getParsecsvdata = (fileData) => {
  data.dataLoading = true;
  try {
    let parsedata = [];
    let newLinebrk = fileData.split("\n");
    for (let i = 0; i < newLinebrk.length; i++) {

      parsedata.push(newLinebrk[i].split(","))
    }
    ApiWrapper.post('payments', 'csv-payments', {
          batchId: props.contextIdA,
          data: parsedata
        })
        .then((response) => {
          if (response.success) {
            data.success = response.success
          }
          if (response.failures) {
            data.failures = response.failures
            if (response.failures.length > 0) {
              let alertString = ''
              for (let x in response.failures) {
                alertString += response.failures[x][0] + ' '
              }
              ToastUtil.errorMsg("The following payments failed: " + alertString)
            }
          }

          getData()
        })
        .catch((err) => {
          ToastUtil.error(err);
        })
        .finally(() => {
          data.dataLoading = false
        })
  }
  catch(e) {
    data.dataLoading = false;
    ToastUtil.errorMsg("Error parsing file");
  }
}

const changeStatus = () => {
  data.statusUpdating = true
  data.dialog = false
  data.dialog2 = false
  if(data.singleBatch.isclosed === true) {
    data.singleBatch.isclosed = false
  } else {
    data.singleBatch.isclosed = true
  }
  ApiWrapper.post('payments', 'change-status', {
        batchId: props.contextIdA,
        newStatus: data.singleBatch.isclosed
      })
      .then((response) => {
        getData()

        ToastUtil.successMsg(data.singleBatch.isclosed ? 'Closed batch' : 'Opened batch');
      })
      .catch((err) => {
        ToastUtil.error(err);
        data.statusUpdating = false;
      })
}

const editItem = (item) => {
  data.editAmount = JSON.parse(JSON.stringify(item.amount))
  data.editId = JSON.parse(JSON.stringify(item.id))
  data.isEditing = true
}

function convertMoneyStringToFloat(moneyString) {
  // Remove currency symbols, commas, and any non-numeric characters except for the decimal point
  const cleanedString = moneyString.replace(/[^\d.]/g, '');

  // Convert the cleaned string to a float
  return parseFloat(cleanedString);
}

const editPayments = () => {
  let amountFloat = convertMoneyStringToFloat(data.editAmount);
  if (form.value.validate() && !isNaN(amountFloat)) {
    data.dataLoading = true;
    ApiWrapper.post('payments', 'edit-payment', {
          amount: amountFloat,
          id: data.editId
        })
        .then((response) => {
          getData();
          discardItem();
          ToastUtil.successMsg('Updated payment');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.dataLoading = false;
        })
  }
}

const discardItem = () => {
  data.editAmount = null
  data.editId = null
  data.isEditing = false;
}

const deleteItem = (item) => {
  let deleteBatch = confirm(
      'Are you sure you want to delete this item?'
  )
  if (deleteBatch === true) {
    data.dataLoading = true;
    ApiWrapper.post('payments', 'delete-payment', {
          item: item,
        })
        .then((response) => {
          getData()
          ToastUtil.successMsg('Deleted payment');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.dataLoading = false;
        })
  }
}

const getData = () => {

  ApiWrapper.post('payments', 'get-batch-data', {
        batchId: props.contextIdA,
      })
      .then((response) => {
        if(response.batchData){
          data.items = response.batchData;
        }
        if(response.batchDataTwo){
          data.singleBatch = response.batchDataTwo;
          if (data.singleBatch.isclosed === null) {
            data.singleBatch.isclosed = false;
          }
          if(data.singleBatch.isclosed === true){
            data.singleBatch.batchstatus = 'Closed'
          } else {
            data.singleBatch.batchstatus = 'Open'
          }
        }
      })
      .catch((e) => {
        ToastUtil.error(e);
      })
      .finally(()=>{
        data.dataLoading = false;
        data.statusUpdating = false;
      })
}

const fileInputChange = (event) => {
  const files = event.target.files;

  if (files && files[0]) {

    let myFile = files[0];
    let reader = new FileReader();

    reader.addEventListener('load', function (e) {

      let csvdata = e.target.result;
      getParsecsvdata(csvdata); // calling function for parse csv data
    });

    reader.readAsBinaryString(myFile);
  }
}

onMounted(()=>{
  getData();
})
</script>

<template>
  <v-container fluid>
    <v-card class="elevation-1" v-if="data.items.length">
      <v-toolbar flat color="white">
        <v-toolbar-title>Payments Per Batch</v-toolbar-title>
        <v-icon @click="csvExport(csvData)" v-if="data.items.length"> mdi-file-download </v-icon>
        <v-divider class="mx-4" inset vertical></v-divider>

        <v-text-field
            class="px-10"
            label="Search"
            type="text"
            v-model="data.search"
            append-icon="mdi-magnify"
        >
        </v-text-field>

        <v-spacer></v-spacer>

        <!-- Close batch dialog button -->
        <v-dialog
            v-if="data.singleBatch.batchstatus === 'Open'"
            v-model="data.dialog"
            persistent
            width="auto"
        >
          <template v-slot:activator="{ on, props }">
            <v-btn
                variant="elevated"
                color="success"
                dark
                v-bind="props"
                v-on="on"
                :loading="data.statusUpdating"
                :disabled="!Permissions.checkPermissions(['payments.change-status'])"
            >
              Status: {{ data.singleBatch.batchstatus }}
              <v-icon
                  right
                  dark
              >
                mdi-checkbox-marked-circle
              </v-icon>
            </v-btn>
          </template>
          <v-card>
            <v-card-title class="headline">
              Confirm that you wish to CLOSE this batch?
            </v-card-title>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                  color="error"
                  variant="text"
                  @click="data.dialog = false"
              >
                Cancel
              </v-btn>
              <v-btn
                  color="success"
                  variant="text"
                  @click="changeStatus"
              >
                Confirm
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Open batch dialog button -->
        <v-dialog
            v-if="data.singleBatch.batchstatus === 'Closed'"
            v-model="data.dialog2"
            persistent
            max-width="290"
        >
          <template v-slot:activator="{ on, props }">
            <v-btn
                v-if="data.singleBatch.batchstatus === 'Closed'"
                class="error"
                color="error"
                dark
                v-bind="props"
                v-on="on"
                :loading="data.statusUpdating"
                :disabled="!Permissions.checkPermissions(['payments.change-status'])"
            >
              Status: {{ data.singleBatch.batchstatus }}
            </v-btn>
          </template>
          <v-card>
            <v-card-title class="headline">
              Confirm that you wish to OPEN this batch?
            </v-card-title>
            <!-- <v-card-title class="headline">
              Reason for re-opening this claim?
            </v-card-title>
            <v-form
              ref="form"
              v-model="valid"
              lazy-validation
            >
              <v-select
                density="compact"
                class="mx-6"
                :items="reasons"
                :rules="[v => !!v || 'Reason is required']"
                label="Select Reason"
                required
              ></v-select>
            </v-form> -->
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                  color="error"
                  variant="text"
                  @click="dialog2 = false"
              >
                Cancel
              </v-btn>
              <v-btn
                  color="success"
                  variant="text"
                  @click="changeStatus"
              >
                Confirm
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

      </v-toolbar>
      <v-card-subtitle>Batch {{props.contextIdA}}</v-card-subtitle>
      <v-row class="px-5 mt-4 mb-2" v-show="data.singleBatch.isclosed === false && !data.statusUpdating && Permissions.checkPermissions(['payments.csv-upload'])">
        <input type="file" id="dealCsv" @input="fileInputChange"/>
      </v-row>

      <v-form ref="form" v-model="data.valid" lazy-validation v-if="data.isEditing">
        <v-row class="px-5">
          <v-col cols="12" sm="6">
            <v-text-field
                type="text"
                v-model="data.editAmount"
                label="Payment Amount"
                :rules="[v => !!v || 'Payment amount is required']"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4">
            <v-btn
                size="small"
                variant="elevated"
                class="px-2"
                @click="editPayments()"
                color="primary"
                :disabled="!data.valid"
            >
              Save
            </v-btn>
            <v-btn size="small" variant="elevated" class="px-2" @click="discardItem()" color="error">
              Cancel
            </v-btn>
          </v-col>
        </v-row>
      </v-form>

      <v-data-table
          :headers="data.headers"
          :items="data.items"
          :search="data.search"
          :loading="data.dataLoading"
          loading-text="Loading... Please wait"
      >
        <template v-slot:[`item.invoiceid`]="{ item }">
          <RouterLink :to="`/claim/${item.claimid}`">
            {{ item.invoiceid }}
          </RouterLink>
        </template>
        <template v-slot:[`item.invoicedate`]="{ item }">
          <span>{{ formatISODateToAmerican(item.invoicedateformatted) }}</span>
        </template>
        <template v-slot:[`item.date`]="{ item }">
          <span>{{ formatISODateToAmerican(item.dateformatted) }}</span>
        </template>
        <template v-slot:[`item.amount`]="{ item }">
          <span>{{ item.amount }}</span>
        </template>
        <template v-slot:[`item.clientid`]="{ item }">
          <RouterLink :to="`/client/${item.clientid}`" v-if="Permissions.checkPermissions(['clients.read'])">
            {{ item.clientname }}
          </RouterLink>
          <template v-else>
            {{ item.clientname }}
          </template>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-icon v-if="Permissions.checkPermissions(['payments.update'])" @click="editItem(item)"> mdi-pencil </v-icon>
          <v-icon v-if="Permissions.checkPermissions(['payments.delete'])" @click="deleteItem(item)"> mdi-delete </v-icon>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
.container {
  padding: 5px;
}
[v-cloak] {
  display: none;
}
</style>